import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Ruler, Layers, Volume2, Trees } from "lucide-react";
import HeroSlider from "../components/HeroSlider";
import LogoSlider from "../components/LogoSlider";
import ProjectCarousel from "../components/ProjectCarousel";
import Reveal from "../components/Reveal";
import Seo, { organizationJsonLd } from "../components/Seo";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";
import { PRODUCT_LINES_L } from "../content";
import { SEO_META } from "../seo";

const ICONS = [Layers, Volume2, Ruler, Trees];

export default function Home() {
  const { t, loc } = useI18n();
  const { projects, content } = useSiteData();
  const featured = projects.slice(0, 10);
  const home = content.home || {};
  const services = content.services?.items || [];
  const stats = content.about?.stats || [];
  const on = (key) => home[key] !== false;
  const txt = (key, fallbackKey) => loc(home[key]) || t(fallbackKey);
  const lines = (home.productLines || []).length ? home.productLines : PRODUCT_LINES_L;

  return (
    <div>
      <Seo
        path="/"
        title={loc(SEO_META.home.title)}
        description={loc(SEO_META.home.description)}
        jsonLd={organizationJsonLd}
      />
      <HeroSlider />

      {/* ---------- STUDIO ---------- */}
      {on("showStudio") && (
        <section className="shell py-24 lg:py-36" data-testid="home-studio">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-3">
              <p className="mono text-[9.5px] text-[#6B675F]">{txt("studioLabel", "label.studio")}</p>
            </div>
            <div className="lg:col-span-9">
              <Reveal>
                <p className="display text-[23px] sm:text-[34px] lg:text-[46px] text-[#141414] leading-[1.06] max-w-[1080px]">
                  {loc(home.studioLead)}
                </p>
              </Reveal>
              <Reveal delay={120}>
                <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8 border-t border-[#E2E0D8] pt-10">
                  {stats.map((s) => (
                    <div key={s.value}>
                      <p className="display text-[34px] lg:text-[44px] text-[#141414]">{s.value}</p>
                      <p className="mono text-[9px] text-[#6B675F] mt-2">{loc(s.label)}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      <LogoSlider page="home" anchor="afterStudio" />

      {/* ---------- PRODUCT LINES ---------- */}
      {on("showProductLines") && (
        <section
          className="bg-[#EFECE6] border-t border-b border-[#E2E0D8] py-24 lg:py-32"
          data-testid="home-product-lines"
        >
          <div className="shell">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
              <div>
                <p className="mono text-[9.5px] text-[#6B675F] mb-5">{txt("productsLabel", "label.products")}</p>
                <h2 className="display text-[34px] sm:text-[52px] lg:text-[70px] text-[#141414] whitespace-pre-line leading-[0.98]">
                  {txt("productsTitle", "home.lines")}
                </h2>
              </div>
              <Link
                to="/products"
                className="mono text-[10px] text-[#141414] inline-flex items-center gap-2 link-underline shrink-0 whitespace-nowrap"
              >
                {txt("productsCta", "cta.allProducts")} <ArrowUpRight size={13} strokeWidth={1.6} />
              </Link>
            </div>

            <div className="flex flex-col gap-6 lg:gap-8">
              {lines.map((p, i) => (
                <Reveal key={p.id || p.name || i} delay={i * 80}>
                  <Link
                    to={p.to || "/products"}
                    data-testid={`product-line-${p.id || i}`}
                    className="group grid grid-cols-1 lg:grid-cols-12 bg-white border border-[#E2E0D8] hover:border-[#C8B29B] transition-colors duration-500"
                  >
                    <div className="lg:col-span-7 media aspect-[16/9] overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="lg:col-span-5 p-7 lg:p-11 flex flex-col justify-center">
                      <div className="flex items-baseline justify-between gap-4">
                        <span className="mono text-[9px] text-[#6B675F]">{loc(p.family)}</span>
                        <span className="mono text-[9px] text-[#A8A29A]">{p.tag}</span>
                      </div>
                      <h3 className="display text-[28px] lg:text-[38px] text-[#141414] mt-4 leading-[1.02]">
                        {p.name}
                      </h3>
                      <p className="text-[14px] text-[#141414]/65 mt-5 leading-relaxed max-w-[480px]">{loc(p.text)}</p>
                      <div className="mt-7 flex flex-wrap gap-2">
                        {(p.specs || []).map((s) => (
                          <span
                            key={s}
                            className="mono text-[8.5px] border border-[#E2E0D8] px-2.5 py-1.5 text-[#141414]/70"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- HIGH IMPACT PRODUCT CTA ---------- */}
      {on("showCta") && (
        <section className="relative border-b border-[#E2E0D8]" data-testid="product-cta">
          <div className="absolute inset-0">
            <img
              src={home.ctaImage || "/match/launch.jpg"}
              alt="MATCH Collection 2026 installed surface"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#141414]/85 via-[#141414]/45 to-[#141414]/10" />
          <div className="relative shell py-28 lg:py-44">
            <div className="max-w-[720px]">
              <p className="mono text-[9.5px] text-[#E6DFD4] mb-7">{txt("ctaLabel", "home.productCta")}</p>
              <h2 className="display text-[36px] sm:text-[54px] lg:text-[76px] text-[#FAFAFA] leading-[0.96] whitespace-pre-line">
                {loc(home.ctaTitle)}
              </h2>
              <p className="text-[15px] lg:text-[17px] text-[#FAFAFA]/80 mt-7 leading-relaxed max-w-[540px]">
                {loc(home.ctaText)}
              </p>
              <Link
                to="/products"
                className="mt-10 inline-flex items-center gap-3 bg-[#FAFAFA] text-[#141414] px-7 py-4 mono text-[10px] hover:bg-white transition-colors duration-300"
              >
                {txt("ctaButton", "cta.explore")} <ArrowUpRight size={15} strokeWidth={1.6} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ---------- CAPABILITIES ---------- */}
      {on("showCapabilities") && (
        <section className="shell py-24 lg:py-32" data-testid="home-capabilities">
          <p className="mono text-[9.5px] text-[#6B675F] mb-12">{txt("capabilitiesLabel", "label.capabilities")}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E0D8] border border-[#E2E0D8]">
            {services.slice(0, 4).map((s, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <Reveal key={s.no} delay={i * 80}>
                  <div className="bg-[#F7F6F2] p-8 lg:p-10 h-full hover:bg-[#FFFFFF] transition-colors duration-500">
                    <div className="flex items-center justify-between mb-10">
                      <Icon size={22} strokeWidth={1.3} className="text-[#141414]" />
                      <span className="mono text-[9px] text-[#A8A29A]">{s.no}</span>
                    </div>
                    <h3 className="text-[19px] font-medium text-[#141414] leading-snug">{loc(s.title)}</h3>
                    <p className="text-[13.5px] text-[#141414]/60 mt-4 leading-relaxed">{loc(s.text)}</p>
                    <Link
                      to="/services"
                      className="mono text-[9px] text-[#141414] mt-8 inline-flex items-center gap-1.5 link-underline"
                    >
                      {t("cta.detail")} <ArrowUpRight size={11} strokeWidth={1.8} />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------- PHILOSOPHY ---------- */}
      {on("showPhilosophy") && (
        <section className="shell py-24 lg:py-36" data-testid="home-philosophy">
          <Reveal>
            <div className="max-w-[1100px]">
              <p className="mono text-[9.5px] text-[#6B675F] mb-8">{txt("philosophyLabel", "label.philosophy")}</p>
              <p className="serif-quote text-[26px] sm:text-[38px] lg:text-[50px] leading-[1.14] text-[#141414]">
                &ldquo;{loc(home.quote)}&rdquo;
              </p>
              <p className="mono text-[10px] text-[#6B675F] mt-8">{home.quoteAuthor}</p>
            </div>
          </Reveal>
        </section>
      )}

      {/* ---------- SELECTED WORK CAROUSEL ---------- */}
      {on("showWork") && (
        <section className="bg-[#F2F0EB] border-t border-[#E2E0D8] py-24 lg:py-32" data-testid="home-work">
          <div className="shell">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
              <div>
                <p className="mono text-[9.5px] text-[#6B675F] mb-5">{txt("workLabel", "label.selectedWork")}</p>
                <h2 className="display text-[34px] sm:text-[52px] lg:text-[70px] text-[#141414]">
                  {txt("workTitle", "home.portfolio")}
                </h2>
              </div>
              <Link
                to="/portfolio"
                className="mono text-[10px] text-[#141414] inline-flex items-center gap-2 link-underline shrink-0 whitespace-nowrap"
              >
                {txt("workCta", "cta.allProjects")} <ArrowUpRight size={13} strokeWidth={1.6} />
              </Link>
            </div>
            <ProjectCarousel projects={featured} />
          </div>
        </section>
      )}

      <LogoSlider page="home" anchor="afterWork" />
      <LogoSlider page="home" anchor="bottom" />
    </div>
  );
}
